import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { api } from '../api/client';

interface InvitationDetails {
  status: 'pending' | 'expired' | 'accepted' | 'revoked';
  familyName: string;
  memberName?: string;
  inviterName?: string;
  email?: string;
  expiresAt?: string;
}

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    async function fetchInvitation() {
      try {
        const data = await api.get<InvitationDetails>(`/invitations/accept/${token}`);
        setInvitation(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load invitation');
      } finally {
        setLoading(false);
      }
    }

    fetchInvitation();
  }, [token]);

  const handleGoogleSignIn = () => {
    window.location.href = `/api/v1/invitations/accept/${token}/google`;
  };

  const handleMicrosoftSignIn = () => {
    window.location.href = `/api/v1/invitations/accept/${token}/microsoft`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-teal animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-semibold text-text-primary">Invalid Invitation</h1>
            <p className="text-text-secondary mt-2">{error}</p>
          </div>
          <div className="card text-center">
            <button
              onClick={() => navigate('/')}
              className="btn btn-primary"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Handle non-pending statuses
  if (invitation?.status === 'expired') {
    navigate('/invite/expired');
    return null;
  }

  if (invitation?.status === 'accepted') {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-semibold text-text-primary">Already Accepted</h1>
            <p className="text-text-secondary mt-2">
              This invitation has already been accepted.
            </p>
          </div>
          <div className="card text-center">
            <p className="text-text-secondary mb-4">
              You can sign in to access {invitation.familyName}.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="btn btn-primary"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (invitation?.status === 'revoked') {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-semibold text-text-primary">Invitation Revoked</h1>
            <p className="text-text-secondary mt-2">
              This invitation has been cancelled by {invitation.familyName}.
            </p>
          </div>
          <div className="card text-center">
            <p className="text-text-secondary mb-4">
              Please contact a family member to request a new invitation.
            </p>
            <button
              onClick={() => navigate('/')}
              className="btn btn-secondary"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Pending invitation - show accept UI
  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal mb-4">
            <Calendar className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-text-primary">Family Calendar</h1>
          <p className="text-text-secondary mt-2">You've been invited!</p>
        </div>

        {/* Invitation Card */}
        <div className="card">
          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold text-text-primary mb-2">
              Join {invitation?.familyName}
            </h2>
            <p className="text-text-secondary">
              <span className="font-medium text-text-primary">{invitation?.inviterName}</span>{' '}
              invited you to join as{' '}
              <span className="font-medium text-text-primary">{invitation?.memberName}</span>
            </p>
          </div>

          <div className="bg-bg-secondary rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-text-primary mb-2">
              After joining, you'll be able to:
            </h3>
            <ul className="text-sm text-text-secondary space-y-1">
              <li>• Connect your Google or Microsoft calendar</li>
              <li>• View and manage family events</li>
              <li>• Complete assigned chores</li>
              <li>• Collaborate on family lists</li>
            </ul>
          </div>

          <p className="text-sm text-text-muted text-center mb-4">
            Sign in with your preferred account to accept
          </p>

          <div className="space-y-3">
            <button
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-border-default rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="text-text-primary font-medium">Continue with Google</span>
            </button>

            <button
              onClick={handleMicrosoftSignIn}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-border-default rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#F25022" d="M1 1h10v10H1z" />
                <path fill="#00A4EF" d="M1 13h10v10H1z" />
                <path fill="#7FBA00" d="M13 1h10v10H13z" />
                <path fill="#FFB900" d="M13 13h10v10H13z" />
              </svg>
              <span className="text-text-primary font-medium">Continue with Microsoft</span>
            </button>
          </div>

          {invitation?.expiresAt && (
            <p className="text-xs text-text-muted text-center mt-6">
              This invitation expires on{' '}
              {new Date(invitation.expiresAt).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
